import Meta from "../../partials/meta";
import styles from "./terms.module.scss";
import { contactEmail, weAreOnNuRealityDomain } from "../../shared";
import { Link } from "react-router-dom";

// Commercial terms DO differ slightly from non-commercial terms.
// Mainly, the Licenses section refers to different documents.
// The other difference is "Website" vs. "Websites"
export const TermsNonCommercial = (): JSX.Element => (
  <div className={styles.bg}>
    <Meta
      title="Terms of use - Non-Commercial"
      description="The legal terms of use for the nuScenes, nuImages, nuPlan, and nuReality datasets."
    />
    <div className="content text-justify">
      <div className={styles.content}>
        <h1>Non-Commercial Use</h1>
        <p>
          nuScenes™, nuPlan™, nuImages™, nuReasoning™, and nuReality™ are
          leading datasets for Autonomous Vehicles (AVs) and are available for
          non-commercial use subject to the Terms of Use below.
        </p>

        <h1>Terms of Use</h1>
        <p>These terms were last updated on Nov 16, 2021</p>
        <h2>Description</h2>
        <p>
          Motional AD Inc. and its affiliates (hereinafter "Motional") strive to
          enhance public access to and use of data that it collects and
          publishes. The data are organized in datasets (the “Datasets”) listed
          at nuScenes.org and nuReality.org (the “Websites”). The Datasets are
          collections of data, managed by Motional and provided in a number of
          machine-readable formats. Motional provides any individual or entity
          (hereinafter You” or “Your”) with access to the Datasets free of
          charge subject to the terms of this agreement (hereinafter “Dataset
          Terms”). Use of any data derived from the Datasets, which may appear
          in any format such as tables and charts, is also subject to these
          Dataset Terms.
        </p>
        <h2>Licenses</h2>
        <p>
          Unless specifically labeled otherwise, these Datasets are provided to
          You under a Creative Commons Attribution-NonCommercial-ShareAlike 4.0
          International Public License (“CC BY-NC-SA 4.0”), with the additional
          terms included herein. The CC BY-NC-SA 4.0 may be accessed at{" "}
          <a
            href="https://creativecommons.org/licenses/by-nc-sa/4.0/legalcode"
            target="_blank"
            rel="noreferrer"
          >
            https://creativecommons.org/licenses/by-nc-sa/4.0/legalcode
          </a>{" "}
          When You download or use the Datasets from the Websites or elsewhere,
          You are agreeing to comply with the terms of CC BY-NC-SA 4.0 as
          applicable, and also agreeing to the Dataset Terms. Where these
          Dataset Terms conflict with the terms of CC BY-NC-SA 4.0, these
          Dataset Terms shall prevail.
        </p>
        <h2>Exceptions for some third-party data</h2>
        <p>
          Some datasets and indicators are provided by third parties and may not
          be redistributed or reused without the consent of the original data
          provider, or may be subject to terms and conditions that are different
          from those described above. Where applicable, these conditions are
          included in the dataset or indicator metadata.
        </p>
        <h2>Comments</h2>
        <p>
          Motional encourages You to share your suggestions and ideas for using
          or facilitating access to the Datasets with Motional. If You wish to
          make other comments, please contact us by sending an email to{" "}
          <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
        </p>
        <h2>Privacy</h2>
        <p>
          Please see our <Link to="/privacy">Privacy Policy</Link> for more
          details.{" "}
          {!weAreOnNuRealityDomain && (
            <span>
              If You have any privacy concerns, including to remove your name or
              other personal information, you may use the form at the{" "}
              <Link to="/privacy-takedown">Takedown Request</Link> page.
            </span>
          )}
        </p>
        <h2>No endorsement</h2>
        <p>
          You may not publicly represent or imply that Motional is participating
          in, or has sponsored, approved, or endorsed the manner or purpose of
          Your use or reproduction of the Datasets without prior written consent
          from Motional. Motional may prosecute, to the fullest extent of the
          law, any use of Datasets in a manner that falsifies, misrepresents,
          disparages, or fraudulently uses the Datasets.
        </p>
        <h2>No association</h2>
        <p>
          You may not use the name, any trade-mark, official mark, official
          emblem, or logo of Motional, or any of its other means of promotion or
          publicity, without Motional's prior written consent nor in any event
          to represent or imply an association or affiliation with Motional.
        </p>
        <h2>Termination</h2>
        <p>
          Motional may terminate Your access to all or any part of the Datasets
          or the Websites at any time, with or without cause, with or without
          notice, effective immediately. If You wish to terminate Your account
          on the Websites, You may notify Motional at{" "}
          <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.{" "}
        </p>
        <p>
          All provisions of the Dataset Terms which by their nature should
          survive termination shall survive termination, including, without
          limitation, ownership provisions, warranty disclaimers, indemnity, and
          limitations of liability.
        </p>
        <h2>Warranties</h2>
        <p>
          THE DATASETS AND WEBSITES (INCLUDING, WITHOUT LIMITATION, ALL CONTENT
          AND MODIFICATIONS OF ORIGINAL DATASETS POSTED ON THE WEBSITES) ARE
          PROVIDED “AS IS” AND “AS AVAILABLE” AND WITHOUT WARRANTY OF ANY KIND,
          EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
          WARRANTIES OF TITLE, NON-INFRINGEMENT, MERCHANTABILITY AND FITNESS FOR
          A PARTICULAR PURPOSE, AND ANY WARRANTIES IMPLIED BY ANY COURSE OF
          PERFORMANCE OR USAGE OF TRADE, ALL OF WHICH ARE EXPRESSLY DISCLAIMED.
          WITHOUT LIMITING THE FOREGOING, MOTIONAL DOES NOT WARRANT THAT: (A)
          THE CONTENT OR MODIFICATIONS TO THE DATASET ARE TIMELY, ACCURATE,
          COMPLETE, RELIABLE OR CORRECT IN THEIR POSTED FORMS AT THE WEBSITES;
          (B) THE WEBSITES WILL BE SECURE; (C) THE WEBSITES WILL BE AVAILABLE AT
          ANY PARTICULAR TIME OR LOCATION; (D) ANY DEFECTS OR ERRORS WILL BE
          CORRECTED; (E) THE WEBSITES, CONTENT OR ANY MODIFICATIONS ARE FREE OF
          VIRUSES OR OTHER HARMFUL COMPONENTS; OR (F) THE RESULTS OF USING THE
          WEBSITES WILL MEET YOUR REQUIREMENTS. YOUR USE OF THE WEBSITES, THE
          DATASETS, AND ANY CONTENT IS SOLELY AT YOUR OWN RISK.
        </p>
        <h2>Limitation of liability</h2>
        <p>
          IN NO EVENT SHALL MOTIONAL AND ITS AFFILIATES, OR THEIR DIRECTORS,
          EMPLOYEES, AGENTS, PARTNERS, OR SUPPLIERS, BE LIABLE UNDER CONTRACT,
          TORT, STRICT LIABILITY, NEGLIGENCE OR ANY OTHER LEGAL THEORY WITH
          RESPECT TO THE WEBSITES, THE DATASETS, OR ANY CONTENT OR USER
          SUBMISSIONS (I) FOR ANY DIRECT DAMAGES, OR (II) FOR ANY LOST PROFITS
          OR SPECIAL, INDIRECT, INCIDENTAL, PUNITIVE, OR CONSEQUENTIAL DAMAGES
          OF ANY KIND WHATSOEVER.
        </p>
        <h2>Indemnification</h2>
        <p>
          You will indemnify and hold Motional harmless from and against any and
          all loss, cost, expense, liability, or damage, including, without
          limitation, all reasonable attorneys’ fees and court costs, arising
          from (i) Your use or misuse of the Websites or the Datasets; (ii) Your
          access to the Websites; (iii) Your violation of the Dataset Terms; or
          (iv) infringement by You, or any third party using Your account, of
          any intellectual property or other right of any person or entity,
          including but not limited to infringements upon any and all
          representations made by You in this Agreement. Such losses, costs,
          expenses, damages, or liabilities shall include, without limitation,
          all actual, general, special, and consequential damages.
        </p>
        <h2>Dispute Resolution</h2>
        <p>
          You and Motional agree that any cause of action arising out of or
          related to the Datasets must commence within one (1) year after the
          cause of action arose; otherwise, such cause of action is permanently
          barred.
        </p>
        <p>
          These Terms of Use shall be governed by and interpreted in accordance
          with the laws of the Commonwealth of Massachusetts (excluding the
          conflict of laws rules thereof). All disputes under these Dataset
          Terms will be resolved in the applicable state or federal courts of
          Massachusetts. You consent to the jurisdiction of such courts and
          waive any jurisdictional or venue defenses otherwise available.
        </p>
        <h2>Integration and severability</h2>
        <p>
          This Agreement is the entire agreement between You and Motional with
          respect to the Datasets and use of the Websites and supersedes all
          prior or contemporaneous communications and proposals (whether oral,
          written, or electronic) between You and Motional with respect to the
          Websites (but excluding the use of any third-party software, widgets,
          and applications that may be subject to a separate end-user license
          agreement). If any provision of the Dataset Terms is found to be
          unenforceable or invalid, that provision will be limited or eliminated
          to the minimum extent necessary so that the Dataset Terms will
          otherwise remain in full force and effect and enforceable.
        </p>
        <h2>Miscellaneous</h2>
        <p>
          Motional may assign, transfer or delegate any of its rights and
          obligations hereunder without consent. No agency, partnership, joint
          venture, or employment relationship is created as a result of the
          Dataset Terms and neither party has any authority of any kind to bind
          the other in any respect outside the specified terms of this
          Agreement. In any action or proceeding to enforce rights under the
          Dataset Terms, the prevailing party will be entitled to recover costs
          and attorneys’ fees.
        </p>
        <p>
          These Dataset Terms may be amended by Motional from time to at our
          sole discretion. Please periodically review the controlling version of
          these Dataset Terms. By continuing to use the Datasets subsequent to
          Motional making available an amended version of these Dataset Terms,
          You acknowledge, agree and consent to such amendment.
        </p>
        <p>
          You agree that it is solely Your responsibility to comply with all
          applicable laws with regard to Your use or publication of the
          Datasets, including any applicable privacy, data protection, security,
          and export laws. You agree to take reasonable steps to assist Motional
          in fulfilling responsibilities to comply with applicable laws with
          regard to use or publication of Datasets.
        </p>
      </div>
    </div>
  </div>
);
export default TermsNonCommercial;
